/* GlowSpine.jsx — SVG replacement for chatboard's dashed-CSS spine.
 *
 * WHAT THIS IS
 *   A single SVG that draws the main forecast spine + all branch spines as
 *   smooth, softly-glowing, gently-undulating curves. It replaces the
 *   border-top:Npx dashed <div> spine + border-left connector + label-gap
 *   masking that lives inside features/timeline/components/ForecastLane.jsx.
 *
 * WHAT IT IS NOT
 *   It does NOT own layout, data, hover state, or the forecast lifecycle. It is
 *   fed a normalized `lines` (and optional `events`) array — derived from the
 *   run-store selectors + laneLayout by the caller — and renders. Hover is
 *   reported back through `onHover` so ForecastLane's existing
 *   hoveredBranch / hoveredMainSpine stays the source of truth.
 *
 *   In chatboard it is rendered SPINE-ONLY (events=[]): the interactive event
 *   pies + tooltips + occurs/not-occurs forking remain the existing renderRow
 *   layer, painted on top. The `events` rendering below is kept intact so a
 *   future "full glow events" mode can light it up without a component change.
 *
 * COORDINATES
 *   Everything is in the timeline's view-relative pixel space — the same space
 *   `dayToScreen(date)` produces. x = horizontal pixel; y = vertical pixel of a
 *   row's spine (laneLayout's spineY for main, branchYs[i] for a branch). Pass
 *   the SVG the full timeline width/height so it overlays the scroll area 1:1.
 *
 * Adapted from the design-handoff prototype (standalone HTML/React) to the
 * app's ES-module import style. The math + look are unchanged.
 */
import React, { useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { eventKey } from "../../lib/forecast/eventIdentity.js";
// GLOW_DEFAULTS lives in the pure model module so this file stays a
// components-only export (react-refresh). In production the live values come
// from props.controls (see GlowSpineControls); GLOW_DEFAULTS is the fallback.
import { GLOW_DEFAULTS, twFor, TRAIL_PX } from "../../lib/timeline/glowSpineModel.js";
import { measureLabelWidth } from "../../lib/timeline/measureText.js";
import { APP_FONT_FAMILY, APP_LETTER_SPACING } from "../../lib/appFont.js";
import { ui } from "../../lib/telemetry/instrument.js";

// ────────────────────────────────────────────────────────────
// Geometry math — the smooth branch peel-away lives here.
// ────────────────────────────────────────────────────────────
const LEAD = 30; // branch path starts this many px before its split, riding
// ON the parent so the start-cap hides under the parent line.

// `phase` desynchronizes each line's undulation so branches don't ripple in
// lockstep with the main spine (and each other). Defaults to 0 (main spine).
function waveAt(x, W, t, o, phase = 0) {
  return o.amp * Math.sin((x / W) * o.freq - t * o.speed + phase);
}
function slopeAt(f, x, e = 0.5) {
  return (f(x + e) - f(x - e)) / (2 * e);
}
// twFor (peel-away transition width) is shared with buildGlowLines — imported
// from the model so the curve's landing point and the model's split stay aligned.

// y of `line` at pixel x. Root = spine + wave. Branch = Hermite curve that
// leaves the parent matching its slope and lands on the branch lane matching
// the wave's slope, so both joins are C1-smooth (no kink, no "stuck-on" look).
function lineY(line, byId, x, W, spineY, t, o) {
  if (x < line.splitX) return null;
  const ph = line.phase; // per-line undulation phase (undefined → 0 in waveAt)
  if (!line.parentId) return spineY + waveAt(x, W, t, o, ph);
  const parent = byId[line.parentId];
  const targetY = line.yOffset;
  const parentSettled = parent.parentId ? parent.yOffset : spineY;
  const TW = twFor(targetY - parentSettled);
  const dx = x - line.splitX;
  if (dx >= TW) return targetY + waveAt(x, W, t, o, ph);
  const u = dx / TW,
    u2 = u * u,
    u3 = u2 * u;
  const h00 = 2 * u3 - 3 * u2 + 1,
    h10 = u3 - 2 * u2 + u,
    h01 = -2 * u3 + 3 * u2,
    h11 = u3 - u2;
  const x0 = line.splitX,
    x1 = line.splitX + TW;
  const p0 = lineY(parent, byId, x0, W, spineY, t, o);
  const p1 = targetY + waveAt(x1, W, t, o, ph);
  const pFn = (xx) => lineY(parent, byId, Math.max(parent.splitX, xx), W, spineY, t, o);
  const m0 = slopeAt(pFn, x0) * TW;
  const m1 = slopeAt((xx) => targetY + waveAt(xx, W, t, o, ph), x1) * TW;
  return h00 * p0 + h10 * m0 + h01 * p1 + h11 * m1;
}
// settled y (no undulation) — use for placing static elements (events, labels).
const restY = (line, byId, x, W, spineY) =>
  lineY(line, byId, x, W, spineY, 0, { ...GLOW_DEFAULTS, amp: 0, speed: 0 });

function buildPath(line, byId, W, spineY, t, o) {
  const to = line.endX;
  const from = line.parentId ? Math.max(byId[line.parentId].splitX, line.splitX - LEAD) : 0;
  const span = to - from;
  if (span <= 0) return "";
  const N = 230,
    pts = [];
  for (let i = 0; i <= N; i++) {
    const x = from + (i / N) * span;
    const y =
      line.parentId && x < line.splitX
        ? lineY(byId[line.parentId], byId, x, W, spineY, t, o)
        : lineY(line, byId, x, W, spineY, t, o);
    pts.push(`${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`);
  }
  return pts.join(" ");
}

function ancestors(id, byId) {
  const c = [];
  let cur = byId[id];
  while (cur) {
    c.push(cur);
    cur = cur.parentId ? byId[cur.parentId] : null;
  }
  return c;
}
// On hover, an ancestor stays bright only from its split up to where the hovered
// chain leaves it. Leaf is bright to its end. Mirrors chainHoveredFlag today.
function brightRange(line, hoveredId, byId) {
  if (!hoveredId) return null;
  const chain = ancestors(hoveredId, byId);
  const idx = chain.findIndex((b) => b.id === line.id);
  if (idx === -1) return null;
  const x0 = line.parentId ? line.splitX : 0;
  const x1 = idx > 0 ? chain[idx - 1].splitX : line.endX;
  return [x0, x1];
}
function piePath(r, p) {
  if (p <= 0) return "";
  if (p >= 1) return `M 0 ${-r} A ${r} ${r} 0 1 1 -0.01 ${-r} Z`;
  const a = -Math.PI / 2 + p * 2 * Math.PI;
  const large = p > 0.5 ? 1 : 0;
  return `M 0 0 L 0 ${-r} A ${r} ${r} 0 ${large} 1 ${(r * Math.cos(a)).toFixed(2)} ${(r * Math.sin(a)).toFixed(2)} Z`;
}
// Label-gap metrics: how much of the glow line is cut away for a label to sit in.
//
// The gap is `labelW(text)` wide, with the line fading out over `LBL_FADE` before it and back
// in over `LBL_FADE` after — so the distance from a glyph to solid line is
// (labelW - the text's real width) / 2 + LBL_FADE, and every px by which labelW over-states
// the text opens the gap by half that on each side.
//
// It used to over-state it a lot: `length * 6.6 + 44`, where 6.6 is IBM Plex Mono's glyph
// advance at 11px with the element's own 0.5px tracking dropped. The 44 then had to cover
// that shortfall plus a margin, and the leftover showed as dead line — worse for SHORT labels,
// since the fixed 44 was spread over fewer characters ("Branch 1" got a wider gap than
// "Generating Forecast").
//
// Measured instead, so LBL_PAD is the actual gap rather than a fudge factor, identical at
// every label length. These are screen px: only the label's CENTRE moves with zoom.
const LBL_PAD = 4,
  LBL_FADE = 8;
// The face and tracking the <text> below actually renders in, which is neither of the values
// that element sets. GlobalStyles forces APP_FONT_FAMILY over its `fontFamily`, and its
// `letterSpacing="0.5"` is an SVG PRESENTATION ATTRIBUTE, which any CSS declaration outranks —
// so the same global rule's 1px default wins there too. (Contrast the CSS spine's label, whose
// 0.5px is an inline style and does win.) Measuring the declared values instead of the
// effective ones is what left the gap over-wide.
const LBL_FONT = {
  fontSize: 12,
  fontFamily: APP_FONT_FAMILY,
  letterSpacing: APP_LETTER_SPACING,
};
const labelW = (txt) => measureLabelWidth(txt, LBL_FONT) + LBL_PAD * 2;

// ────────────────────────────────────────────────────────────
// The component.
//
// Props:
//   width, height        timeline pixel dimensions (overlay 1:1 with scroll area)
//   spineY               main spine y (this lane's spineY)
//   lines[]              { id, parentId|null, splitX, yOffset, endX, label|null, generating, birth }
//   events[]             { id, lineId, x, kind:'past'|'trigger'|'future'|'forecast', p, dir:'up'|'down' }
//   tokens               { purple, purpleL, today, gray, grayL, bg }  (the app's C)
//   controls             GLOW_DEFAULTS shape (from GlowSpineControls or hardcoded)
//   hoveredId            currently-hovered line id (hoveredBranch, 'main' for root)
//   onHover(id|null)     report hover in/out
//   onForkClick(lineId, x, dir, evt)   omitted in chatboard (display-only spine)
// ────────────────────────────────────────────────────────────
export default function GlowSpine({
  width: W,
  height: H,
  spineY,
  lines,
  events = [],
  tokens: C,
  controls = GLOW_DEFAULTS,
  hoveredId = null,
  onHover = () => {},
  onForkClick = null,
}) {
  const o = { ...GLOW_DEFAULTS, ...controls };
  const byId = useMemo(() => {
    const m = {};
    lines.forEach((l) => (m[l.id] = l));
    return m;
  }, [lines]);
  const anyHover = !!hoveredId;

  // refs for the rAF loop (path d + entrance reveal are imperatively updated)
  const R = useRef({});
  const set = (id, slot) => (el) => {
    R.current[`${id}-${slot}`] = el;
  };
  const linesRef = useRef(lines);
  const byIdRef = useRef(byId);
  const oRef = useRef(o);
  // Live-mirror the latest props into refs so the single rAF loop below always
  // reads current values without re-subscribing every render. Written in an
  // effect (after render), not during render, per react-hooks/refs.
  useEffect(() => {
    linesRef.current = lines;
    byIdRef.current = byId;
    oRef.current = o;
  });

  const ENTRANCE = 1100;
  // Entrance births are tracked HERE (in the rAF loop, an effect — never during
  // render, so no impure clock call in render). The first frame stamps every
  // line already present as settled (birth far in the past → no sweep replay,
  // mirroring how loading a saved forecast renders settled); a line whose id
  // first appears on a later frame animates in from that frame's `now`.
  const birthsRef = useRef({});
  const firstTickRef = useRef(true);
  // Last wave phase (seconds) the rAF loop computed. The geometry-sync layout
  // effect below reuses it so a synchronously-rebuilt path keeps the same
  // undulation phase the animation is currently at (no visible phase jump).
  const tRef = useRef(0);
  useEffect(() => {
    let raf,
      t0 = null,
      stopped = false;
    const tick = (now) => {
      if (stopped) return;
      if (t0 == null) t0 = now; // seed the wave clock from the first rAF frame
      const t = (now - t0) / 1000,
        opt = oRef.current,
        ls = linesRef.current,
        bid = byIdRef.current;
      tRef.current = t; // remember the wave phase for the geometry-sync layout effect
      const seen = birthsRef.current;
      const initial = firstTickRef.current;
      ls.forEach((l) => {
        const d = buildPath(l, bid, W, spineY, t, opt);
        ["halo0", "halo1", "halo2", "line", "bright", "hit"].forEach((s) => {
          const el = R.current[`${l.id}-${s}`];
          if (el) el.setAttribute("d", d);
        });
        // entrance sweep L→R via a clip rect whose width grows, + group opacity.
        if (seen[l.id] == null) seen[l.id] = initial ? now - ENTRANCE - 1000 : now;
        const age = now - seen[l.id];
        const u = Math.max(0, Math.min(1, age / ENTRANCE));
        const eased = 1 - Math.pow(1 - u, 3);
        const from = l.parentId ? Math.max(bid[l.parentId].splitX, l.splitX - LEAD) : 0;
        const revealX = from + (l.endX - from) * eased;
        const rect = R.current[`${l.id}-revealrect`];
        if (rect) rect.setAttribute("width", String(Math.max(0, revealX - (from - 40))));
        const grp = R.current[`${l.id}-group`];
        if (grp) grp.style.opacity = String(l.parentId ? eased : Math.min(1, eased * 1.4));
      });
      firstTickRef.current = false;
      raf = requestAnimationFrame(tick);
    };
    // Paths render on the first rAF frame (~16ms); the initial render shows d=""
    // for that brief tick. The frame `now` is the DOMHighResTimeStamp clock used
    // for both the wave (t0) and entrance births above.
    raf = requestAnimationFrame(tick);
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
    };
  }, [W, spineY]);

  // Zoom-artifact guard: the trailing-fade mask, label gaps, and reveal clips are
  // React-rendered (they move to the new endX/splitX the instant geometry changes,
  // e.g. during a zoom), but the path `d` is updated imperatively in the rAF loop —
  // one frame later. For that frame the mask leads the path, so the line's hard
  // right end pokes out past the fade and flashes bright on the right until the
  // next tick realigns it. Rebuild the paths SYNCHRONOUSLY here (same commit as the
  // mask) at the current wave phase, so path and mask always move together. The rAF
  // loop still owns the per-frame undulation + entrance reveal between renders.
  useLayoutEffect(() => {
    const t = tRef.current;
    // Wave params via oRef (like the rAF loop) so this depends only on the
    // GEOMETRY that moves the mask — resyncing on every render (o is a fresh
    // object each time) is unnecessary and would churn.
    lines.forEach((l) => {
      const d = buildPath(l, byId, W, spineY, t, oRef.current);
      ["halo0", "halo1", "halo2", "line", "bright", "hit"].forEach((s) => {
        const el = R.current[`${l.id}-${s}`];
        if (el) el.setAttribute("d", d);
      });
    });
  }, [lines, byId, W, spineY]);

  const lStop = Math.max(0, Math.min(0.5, o.fadeLeft));
  const rStop = 1 - Math.max(0, Math.min(0.5, o.fadeRight));

  function look(l) {
    const isRoot = !l.parentId;
    const onChain = hoveredId ? ancestors(hoveredId, byId).some((a) => a.id === l.id) : false;
    if (!anyHover) {
      return isRoot
        ? { stroke: "#e8ecf2", glow: "#ffffff", go: 0.9, lo: 1 }
        : { stroke: "rgba(170,187,204,0.7)", glow: "#c9b8ff", go: 0.5, lo: 0.92 };
    }
    return onChain
      ? { stroke: "#f6f9ff", glow: "#ffffff", go: 1, lo: 1 }
      : { stroke: "rgba(102,119,136,0.32)", glow: "#000000", go: 0, lo: 1 };
  }
  function activeLabel(l) {
    // label sits centered over the line's visible span
    if (l.generating) return { text: "Forecasting…", cx: (l.splitX + l.endX) / 2, gen: true };
    if (l.label) return { text: l.label, cx: (l.splitX + l.endX) / 2, gen: false };
    return null;
  }
  function handleFork(e, l) {
    if (!onForkClick) return;
    e.stopPropagation();
    const svg = e.currentTarget.ownerSVGElement;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const loc = pt.matrixTransform(svg.getScreenCTM().inverse());
    onForkClick(l.id, loc.x, e.altKey ? "up" : "down", e);
  }

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      style={{ display: "block", overflow: "visible" }}
    >
      <defs>
        <linearGradient id="gs-edge" x1="0" x2="1">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset={lStop} stopColor="#fff" stopOpacity="1" />
          <stop offset={rStop} stopColor="#fff" stopOpacity="1" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <mask id="gs-edge-mask">
          <rect x="0" y={-H} width={W} height={H * 3} fill="url(#gs-edge)" />
        </mask>
        <filter id="gs-big" x="-20%" y="-400%" width="140%" height="900%">
          <feGaussianBlur stdDeviation={6 * o.glow + 0.01} />
        </filter>
        <filter id="gs-med" x="-20%" y="-400%" width="140%" height="900%">
          <feGaussianBlur stdDeviation={3 * o.glow + 0.01} />
        </filter>
        <filter id="gs-sm" x="-20%" y="-400%" width="140%" height="900%">
          <feGaussianBlur stdDeviation={1.3} />
        </filter>

        {lines.map((l) => {
          const al = activeLabel(l);
          const hasHead = !!l.parentId;
          const hx0 = l.splitX,
            hx1 = l.splitX + 34;
          let gapL, gapR;
          if (al) {
            const hw = labelW(al.text) / 2;
            gapL = al.cx - hw;
            gapR = al.cx + hw;
          }
          return (
            <React.Fragment key={`lm-${l.id}`}>
              {hasHead && (
                <linearGradient
                  id={`gs-hf-${l.id}`}
                  gradientUnits="userSpaceOnUse"
                  x1={hx0}
                  y1="0"
                  x2={hx1}
                  y2="0"
                >
                  <stop offset="0" stopColor="#000" />
                  <stop offset="1" stopColor="#fff" />
                </linearGradient>
              )}
              {al && (
                <>
                  <linearGradient
                    id={`gs-lgl-${l.id}`}
                    gradientUnits="userSpaceOnUse"
                    x1={gapL - LBL_FADE}
                    y1="0"
                    x2={gapL}
                    y2="0"
                  >
                    <stop offset="0" stopColor="#fff" />
                    <stop offset="1" stopColor="#000" />
                  </linearGradient>
                  <linearGradient
                    id={`gs-lgr-${l.id}`}
                    gradientUnits="userSpaceOnUse"
                    x1={gapR}
                    y1="0"
                    x2={gapR + LBL_FADE}
                    y2="0"
                  >
                    <stop offset="0" stopColor="#000" />
                    <stop offset="1" stopColor="#fff" />
                  </linearGradient>
                </>
              )}
              <linearGradient
                id={`gs-tr-${l.id}`}
                gradientUnits="userSpaceOnUse"
                x1={l.endX - TRAIL_PX}
                y1="0"
                x2={l.endX}
                y2="0"
              >
                <stop offset="0" stopColor="#fff" />
                <stop offset="1" stopColor="#000" />
              </linearGradient>
              <mask
                id={`gs-lm-${l.id}`}
                maskUnits="userSpaceOnUse"
                x={-60}
                y={-H}
                width={W + 120}
                height={H * 3}
              >
                <rect x={-60} y={-H} width={W + 120} height={H * 3} fill="#fff" />
                {hasHead && (
                  <>
                    <rect x={hx0 - 60} y={-H} width={60} height={H * 3} fill="#000" />
                    <rect
                      x={hx0}
                      y={-H}
                      width={hx1 - hx0}
                      height={H * 3}
                      fill={`url(#gs-hf-${l.id})`}
                    />
                  </>
                )}
                {al && (
                  <>
                    <rect
                      x={gapL - LBL_FADE}
                      y={-H}
                      width={LBL_FADE}
                      height={H * 3}
                      fill={`url(#gs-lgl-${l.id})`}
                    />
                    <rect
                      x={gapL}
                      y={-H}
                      width={Math.max(0, gapR - gapL)}
                      height={H * 3}
                      fill="#000"
                    />
                    <rect
                      x={gapR}
                      y={-H}
                      width={LBL_FADE}
                      height={H * 3}
                      fill={`url(#gs-lgr-${l.id})`}
                    />
                  </>
                )}
                {/* Trailing fade: the line fades out over the last TRAIL_PX (past
                    the forecast line) instead of ending hard, like the dashed trail. */}
                <rect
                  x={l.endX - TRAIL_PX}
                  y={-H}
                  width={TRAIL_PX}
                  height={H * 3}
                  fill={`url(#gs-tr-${l.id})`}
                />
              </mask>
            </React.Fragment>
          );
        })}
        {lines.map((l) => {
          const from = l.parentId ? l.splitX - LEAD : 0;
          return (
            <clipPath key={`rc-${l.id}`} id={`gs-rc-${l.id}`}>
              <rect ref={set(l.id, "revealrect")} x={from - 40} y={-H} width="0" height={H * 3} />
            </clipPath>
          );
        })}
        {lines.map((l) => {
          const r = brightRange(l, hoveredId, byId);
          if (!r) return null;
          return (
            <clipPath key={`bc-${l.id}`} id={`gs-bc-${l.id}`}>
              <rect x={r[0]} y={-H} width={Math.max(0, r[1] - r[0])} height={H * 3} />
            </clipPath>
          );
        })}
      </defs>

      {/* Lines, inside the edge fade */}
      <g mask="url(#gs-edge-mask)">
        {lines.map((l) => {
          const a = look(l);
          const sw = l.parentId ? 1.3 : 1.6;
          const inner = (
            <>
              <path
                ref={set(l.id, "halo0")}
                d=""
                stroke={a.glow}
                strokeWidth={sw * 2.6}
                fill="none"
                strokeLinecap="butt"
                opacity={0.3 * a.go * o.glow}
                filter="url(#gs-big)"
                style={{ transition: "opacity .3s" }}
              />
              <path
                ref={set(l.id, "halo1")}
                d=""
                stroke={a.glow}
                strokeWidth={sw * 1.7}
                fill="none"
                strokeLinecap="butt"
                opacity={0.5 * a.go * o.glow}
                filter="url(#gs-med)"
                style={{ transition: "opacity .3s" }}
              />
              <path
                ref={set(l.id, "halo2")}
                d=""
                stroke={a.glow}
                strokeWidth={sw}
                fill="none"
                strokeLinecap="butt"
                opacity={0.8 * a.go * o.glow}
                filter="url(#gs-sm)"
                style={{ transition: "opacity .3s" }}
              />
              <path
                ref={set(l.id, "line")}
                d=""
                stroke={a.stroke}
                strokeWidth={sw}
                fill="none"
                strokeLinecap="butt"
                opacity={a.lo}
                style={{ transition: "stroke .25s, opacity .25s" }}
              />
              {hoveredId && brightRange(l, hoveredId, byId) && (
                <path
                  ref={set(l.id, "bright")}
                  d=""
                  stroke="#ffffff"
                  strokeWidth={sw + 0.2}
                  fill="none"
                  strokeLinecap="butt"
                  clipPath={`url(#gs-bc-${l.id})`}
                  opacity="0.95"
                />
              )}
              <path
                ref={set(l.id, "hit")}
                {...ui("timeline.spine-line", l.id)}
                d=""
                stroke="transparent"
                strokeWidth="20"
                fill="none"
                style={{ cursor: onForkClick ? "crosshair" : "pointer", pointerEvents: "stroke" }}
                onClick={(e) => handleFork(e, l)}
                onMouseEnter={() => onHover(l.id)}
                onMouseLeave={() => onHover(null)}
              />
            </>
          );
          return (
            <g
              key={l.id}
              ref={set(l.id, "group")}
              clipPath={`url(#gs-rc-${l.id})`}
              style={{ opacity: 0 }}
            >
              <g mask={`url(#gs-lm-${l.id})`}>{inner}</g>
            </g>
          );
        })}
      </g>

      {/* Labels — over the line, in the faded gap */}
      {lines.map((l) => {
        const al = activeLabel(l);
        if (!al) return null;
        const onChain = hoveredId ? ancestors(hoveredId, byId).some((a) => a.id === l.id) : false;
        const ly = (restY(l, byId, al.cx, W, spineY) ?? l.yOffset) + 4;
        return (
          <text
            key={`lbl-${l.id}`}
            x={al.cx}
            y={ly}
            textAnchor="middle"
            fill={al.gen ? C.grayL : C.gray}
            fillOpacity={al.gen ? 0.9 : anyHover ? (onChain ? 0.95 : 0.25) : 0.7}
            fontSize="12"
            letterSpacing="0"
            fontFamily="'IBM Plex Mono',monospace"
            style={{ transition: "fill-opacity .25s" }}
          >
            {al.text}
          </text>
        );
      })}

      {/* Events — past ON the line; trigger/future/forecast OFF the line on a stem.
          In chatboard this is fed events=[] (the interactive pies stay the
          existing renderRow layer); kept intact for a future full-glow mode. */}
      <g>
        {events.map((e) => {
          const l = byId[e.lineId];
          if (!l || l.generating) return null;
          const ey = restY(l, byId, e.x, W, spineY);
          if (ey == null) return null;
          const onChain = hoveredId
            ? ancestors(hoveredId, byId).some((a) => a.id === e.lineId)
            : false;
          const dim = anyHover && !onChain;
          if (e.kind === "past") {
            return (
              <g key={eventKey(e)} transform={`translate(${e.x} ${ey})`}>
                <circle
                  r="8"
                  fill={C.bg}
                  stroke={C.gray}
                  strokeOpacity={dim ? 0.3 : 0.85}
                  strokeWidth="1.5"
                  style={{ transition: "stroke-opacity .25s" }}
                />
              </g>
            );
          }
          const isFC = e.kind === "forecast",
            isTrig = e.kind === "trigger";
          const r = isFC ? 16 : 11;
          const CHAN = 38,
            off = e.dir === "up" ? -CHAN : CHAN;
          const col = isTrig ? C.purpleL : C.purple;
          const stemTo = off - Math.sign(off) * r;
          return (
            <g
              key={eventKey(e)}
              transform={`translate(${e.x} ${ey})`}
              style={{ transition: "opacity .25s" }}
            >
              <line
                x1="0"
                y1="0"
                x2="0"
                y2={stemTo}
                stroke={col}
                strokeOpacity={dim ? 0.18 : 0.45}
                strokeWidth="1.4"
              />
              <circle r="2.4" fill={col} opacity={dim ? 0.3 : 0.9} />
              <g transform={`translate(0 ${off})`}>
                {isTrig ? (
                  <>
                    <circle
                      r={r}
                      fill={C.bg}
                      stroke={col}
                      strokeOpacity={dim ? 0.3 : 0.95}
                      strokeWidth="1.5"
                    />
                    <text
                      x="0"
                      y="5"
                      textAnchor="middle"
                      fontSize="15"
                      fontWeight="700"
                      fill={col}
                      opacity={dim ? 0.3 : 1}
                      fontFamily="'IBM Plex Mono',monospace"
                    >
                      {e.p >= 0.5 ? "✓" : "✕"}
                    </text>
                  </>
                ) : (
                  <>
                    <circle
                      r={r}
                      fill={C.bg}
                      stroke={col}
                      strokeOpacity={dim ? 0.3 : 0.9}
                      strokeWidth="1.5"
                    />
                    <path d={piePath(r - 2, e.p)} fill={col} opacity={dim ? 0.25 : 0.85} />
                  </>
                )}
                {isFC && (
                  <text
                    x={r + 7}
                    y="6"
                    fill={col}
                    fontSize="18"
                    fontWeight="700"
                    fontFamily="'IBM Plex Mono',monospace"
                    opacity={dim ? 0.3 : 1}
                  >
                    {Math.round(e.p * 100)}%
                  </text>
                )}
              </g>
            </g>
          );
        })}
      </g>
    </svg>
  );
}
